/**
 * Resolve which Create Request steps Summary should show (#997 / #1330).
 * Mirrors sidebar nav scope and treats entered field data as an explicit signal.
 */

import { resolveEnabledStepIds } from './resolveRequestFormSteps';
import {
  getDraftRackCountFromFormValues,
  getStepFieldProgress,
} from './sidebarStepProgress';
import {
  OPTIONAL_REQUEST_FORM_STEP_IDS,
  sortStepIdsByRegistry,
  type RequestFormStepId,
} from '../config/requestFormStepRegistry';

export interface SummaryEnabledStepIdSources {
  formData?: Record<string, unknown> | null;
  request?: { enabledStepIds?: string[] | null } | null;
  editedData?: Record<string, unknown>;
  isEditing?: boolean;
  /** Live create-flow mirror from {@link SidebarStepMetricsContext}. */
  formValues?: Record<string, unknown> | null;
  requestScope?: {
    storedStepIds?: string[] | null | undefined;
    enabledStepIds?: readonly string[];
    removableStepIds?: readonly string[];
  } | null;
  /**
   * #997 Bug 19: count of saved Liquid Cooling CDU configurations. When > 0 the
   * Summary enables `liquid-cooling` even if `enabledStepIds` omits it (legacy /
   * removed-scope data), mirroring the Racks config-count signal, so the per-CDU
   * editor summary renders instead of the legacy flat card or a blank section.
   */
  liquidCoolingCduCount?: number;
}

function addResolvedStepIds(
  merged: Set<string>,
  stored: string[] | null | undefined,
): void {
  if (stored === undefined) {
    return;
  }
  for (const id of resolveEnabledStepIds(stored)) {
    merged.add(id);
  }
}

/** Steps with user-entered values should appear in Summary when already in scope. */
function addStepsWithFieldProgress(
  merged: Set<string>,
  scopeStepIds: Set<string>,
  values: Record<string, unknown> | null | undefined,
): void {
  if (!values) {
    return;
  }

  for (const stepId of OPTIONAL_REQUEST_FORM_STEP_IDS) {
    if (!scopeStepIds.has(stepId)) {
      continue;
    }
    const progress = getStepFieldProgress(stepId, values);
    if (progress != null && progress.filled > 0) {
      merged.add(stepId);
    }
  }

  if (scopeStepIds.has('rack-power')) {
    const draftRackCount = getDraftRackCountFromFormValues(values);
    if (draftRackCount != null && draftRackCount > 0) {
      merged.add('rack-power');
    }
  }
}

function mergeEnabledStepIdSets(
  merged: Set<string>,
  ...sources: Array<readonly string[] | string[] | null | undefined>
): void {
  for (const ids of sources) {
    if (ids === undefined) {
      continue;
    }
    for (const id of resolveEnabledStepIds(ids as string[] | null)) {
      merged.add(id);
    }
  }
}

/**
 * Create / edit flow: match Sidebar.tsx — {@link RequestScopeContext.enabledStepIds}
 * is the primary source; union form state and field progress as fallbacks (#1330).
 */
export function resolveCreateFlowSummaryEnabledStepIds(
  requestScope: NonNullable<SummaryEnabledStepIdSources['requestScope']>,
  formData?: Record<string, unknown> | null,
  formValues?: Record<string, unknown> | null,
  request?: SummaryEnabledStepIdSources['request'],
): RequestFormStepId[] {
  const merged = new Set<string>();

  mergeEnabledStepIdSets(
    merged,
    requestScope.enabledStepIds,
    requestScope.removableStepIds,
    requestScope.storedStepIds,
  );

  if (request?.enabledStepIds !== undefined) {
    mergeEnabledStepIdSets(merged, request.enabledStepIds);
  }

  if (formData?.enabledStepIds !== undefined) {
    mergeEnabledStepIdSets(merged, formData.enabledStepIds as string[] | null);
  }

  if (formValues?.enabledStepIds !== undefined) {
    mergeEnabledStepIdSets(
      merged,
      formValues.enabledStepIds as string[] | null,
    );
  }

  const scopeBeforeProgress = new Set(merged);
  addStepsWithFieldProgress(
    merged,
    scopeBeforeProgress,
    formValues ?? formData ?? null,
  );

  return sortStepIdsByRegistry(Array.from(merged)) as RequestFormStepId[];
}

function resolveBaseSummaryEnabledStepIds(
  sources: SummaryEnabledStepIdSources,
): RequestFormStepId[] {
  if (sources.requestScope) {
    return resolveCreateFlowSummaryEnabledStepIds(
      sources.requestScope,
      sources.formData,
      sources.formValues,
      sources.request,
    );
  }

  const merged = new Set<string>();

  if (sources.isEditing && sources.editedData?.enabledStepIds !== undefined) {
    addResolvedStepIds(
      merged,
      sources.editedData.enabledStepIds as string[] | null,
    );
  }

  if (sources.formData?.enabledStepIds !== undefined) {
    addResolvedStepIds(
      merged,
      sources.formData.enabledStepIds as string[] | null,
    );
  }

  if (sources.request) {
    addResolvedStepIds(
      merged,
      sources.request.enabledStepIds !== undefined
        ? sources.request.enabledStepIds
        : null,
    );
  }

  if (sources.formValues?.enabledStepIds !== undefined) {
    addResolvedStepIds(
      merged,
      sources.formValues.enabledStepIds as string[] | null,
    );
  }

  const scopeBeforeProgress = new Set(merged);
  const progressValues =
    sources.formValues ?? sources.formData ?? sources.request ?? null;
  addStepsWithFieldProgress(
    merged,
    scopeBeforeProgress,
    progressValues as Record<string, unknown>,
  );

  return sortStepIdsByRegistry(Array.from(merged)) as RequestFormStepId[];
}

export function resolveSummaryEffectiveEnabledStepIds(
  sources: SummaryEnabledStepIdSources,
): RequestFormStepId[] {
  const resolved = resolveBaseSummaryEnabledStepIds(sources);

  // #997 Bug 19: saved CDU units are a config-count signal — enable Liquid Cooling
  // whenever they exist, even if `enabledStepIds` omits it. Additive only (never
  // removes a step); re-sorted to registry order.
  if (
    (sources.liquidCoolingCduCount ?? 0) > 0 &&
    !resolved.includes('liquid-cooling')
  ) {
    return sortStepIdsByRegistry([
      ...resolved,
      'liquid-cooling',
    ]) as RequestFormStepId[];
  }

  return resolved;
}
